import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, ShieldCheck, Copy, Check, KeyRound } from "lucide-react";
import api, { apiError, passwordError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang, LangToggle } from "../context/LanguageContext";
import { BrandMark } from "../components/Layout";

const HERO = "https://images.pexels.com/photos/442158/pexels-photo-442158.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function OrangeButton({ children, className = "", ...props }) {
  return (
    <button {...props} className={`w-full bg-[#F26722] hover:bg-[#D95513] disabled:opacity-60 text-white font-bold py-3.5 px-4 rounded-xl transition-colors flex items-center justify-center gap-2 ${className}`}>
      {children}
    </button>
  );
}

export default function Login({ adminMode = false }) {
  const nav = useNavigate();
  const { loginSuccess } = useAuth();
  const { t } = useLang();

  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);

  const [enroll, setEnroll] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingToken, setPendingToken] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [ack, setAck] = useState(false);
  const [newPw, setNewPw] = useState("");

  const finishLogin = (token, user) => {
    if (adminMode && user.role !== "super_admin") {
      toast.error(t("This account is not a super-admin."));
      localStorage.removeItem("arv_token");
      return;
    }
    loginSuccess(token, user);
    toast.success(`${t("Welcome,")} ${user.name}`);
    nav(user.role === "super_admin" ? "/admin" : "/reports");
  };

  const proceed = async (token, user) => {
    localStorage.setItem("arv_token", token);
    setPendingToken(token); setPendingUser(user);
    if (user.must_change_password) { setStep("change"); return; }
    if (!user.mfa_enabled) { const s = await api.post("/auth/2fa/setup"); setEnroll(s.data); setStep("enroll"); return; }
    finishLogin(token, user);
  };

  const submitChange = async (e) => {
    e.preventDefault();
    const pe = passwordError(newPw); if (pe) return toast.error(pe);
    setLoading(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: password, new_password: newPw });
      setPassword(newPw);
      await proceed(data.token, data.user);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const submitCreds = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim().toLowerCase(), password });
      if (data.mfa_required) { setChallenge(data.challenge_token); setStep("totp"); }
      else await proceed(data.token, data.user);
    } catch (err) {
      const st = err.response?.status;
      const detail = err.response?.data?.detail;
      if (st === 403 && detail?.code === "COMPANY_APPROVAL_PENDING") toast.error(detail.message || t("Your company is awaiting administrator approval."));
      else if (st === 429) toast.error(t("Too many attempts, please wait a few minutes."));
      else toast.error(apiError(err));
    }
    finally { setLoading(false); }
  };

  const submitTotp = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const { data } = await api.post("/auth/login/2fa", { challenge_token: challenge, code: code.trim() }); await proceed(data.token, data.user); }
    catch (err) { const st = err.response?.status; if (st === 429) toast.error(t("Too many attempts, please wait a few minutes.")); else toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const submitEnable = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const { data } = await api.post("/auth/2fa/enable", { code: code.trim() }); setBackupCodes(data.backup_codes || []); setStep("backup"); }
    catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const copyKey = () => { navigator.clipboard.writeText(enroll.manual_key); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const accent = adminMode ? "text-amber-500" : "text-[#F26722]";

  return (
    <div className="min-h-screen bg-[#0A0A0A] md:grid md:grid-cols-2">
      {/* Desktop hero panel */}
      <div className="surface-dark hidden md:flex relative flex-col justify-between p-12 overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A]/50 via-[#0A0A0A]/70 to-[#0A0A0A]" />
        <div className="relative flex items-center gap-3">
          <Link to="/" data-testid="login-logo-home"><BrandMark company={null} /></Link>
        </div>
        <div className="relative">
          <h2 className="text-4xl xl:text-5xl font-extrabold text-white leading-[1.1]">Field service<br />reporting, done<br />on site.</h2>
          <p className="text-zinc-300 mt-4 max-w-md text-base">Log jobs, machines & parts, capture signatures and send reports straight to the office — secured with authenticator 2FA.</p>
          <div className="flex gap-6 mt-8 text-zinc-400 text-sm">
            <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#F26722]" /> 2FA secured</span>
            <span className="flex items-center gap-2"><Check size={16} className="text-[#F26722]" /> Live with the mobile app</span>
          </div>
        </div>
      </div>

      {/* Form column */}
      <div className="flex flex-col justify-center min-h-screen md:min-h-0 w-full">
        <div className="w-full max-w-md mx-auto md:px-10">
          <div className="flex justify-end px-6 md:px-0 pt-4 md:pt-0"><LangToggle /></div>
          {step === "credentials" && (
            <>
              <div className="surface-dark md:hidden relative h-56 overflow-hidden">
                <img src={HERO} alt="" className="w-full h-full object-cover opacity-40" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0A0A0A]" />
                <div className="absolute top-6 left-4 flex items-center gap-3">
                  <Link to="/" data-testid="login-logo-home-mobile"><BrandMark company={null} /></Link>
                </div>
              </div>
              <form onSubmit={submitCreds} className="px-6 md:px-0 -mt-8 md:mt-0 relative fade-up">
                <h1 className="text-4xl font-extrabold text-white">{adminMode ? t("Admin Sign in") : t("Welcome back")}</h1>
                <p className="text-zinc-400 mt-1">{adminMode ? t("Super-admin control console") : t("Sign in to log a completed job to the office")}</p>
                <label className="block text-xs font-bold tracking-wider text-zinc-400 uppercase mt-8 mb-2">{t("Email")}</label>
                <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                  className="w-full bg-[#1c1c1f] border border-zinc-800 text-white rounded-xl px-4 py-3.5 placeholder-zinc-600 focus:border-[#F26722] focus:outline-none" />
                <label className="block text-xs font-bold tracking-wider text-zinc-400 uppercase mt-5 mb-2">{t("Password")}</label>
                <div className="relative">
                  <input data-testid="login-password-input" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                    className="w-full bg-[#1c1c1f] border border-zinc-800 text-white rounded-xl px-4 py-3.5 pr-12 placeholder-zinc-600 focus:border-[#F26722] focus:outline-none" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400">{showPw ? <EyeOff size={20} /> : <Eye size={20} />}</button>
                </div>
                <div className="text-right mt-3"><Link to="/forgot-password" className={`text-sm font-semibold ${accent}`} data-testid="forgot-password-link">{t("Forgot password?")}</Link></div>
                <OrangeButton data-testid="login-submit-btn" type="submit" disabled={loading} className="mt-6">{loading ? <Loader2 className="animate-spin" size={20} /> : t("Log In")}</OrangeButton>
                {!adminMode && <p className="text-center text-zinc-500 mt-6 text-sm">{t("Admin?")} <Link to="/admin/login" className={`font-bold ${accent}`}>{t("Admin console")}</Link></p>}
                {!adminMode && <p className="text-center text-zinc-500 mt-3 text-sm">{t("New to RV Service Tool?")} <Link to="/register" className={`font-bold ${accent}`} data-testid="go-register-link">{t("Create an account")}</Link></p>}
              </form>
            </>
          )}

          {step === "change" && (
            <form onSubmit={submitChange} className="px-6 md:px-0 py-16 md:py-0 fade-up">
              <div className="flex items-center gap-2 text-[#F26722] font-bold mb-2"><KeyRound size={22} /> {t("Update password")}</div>
              <h1 className="text-3xl font-extrabold text-white">{t("Set a new password")}</h1>
              <p className="text-zinc-400 text-sm mt-1">{t("Your account requires a new password before continuing.")}</p>
              <input data-testid="change-password-input" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder={t("New password (10+ chars, letters & numbers)")}
                className="w-full bg-[#141416] border border-zinc-800 text-white rounded-xl px-4 py-3.5 mt-6 focus:border-[#F26722] focus:outline-none" />
              <OrangeButton data-testid="change-password-btn" type="submit" disabled={loading} className="mt-4">{loading ? <Loader2 className="animate-spin" size={20} /> : t("Update & continue")}</OrangeButton>
            </form>
          )}

          {step === "totp" && (
            <form onSubmit={submitTotp} className="px-6 md:px-0 py-16 md:py-0 fade-up">
              <div className="flex items-center gap-2 text-[#F26722] font-bold mb-2"><ShieldCheck size={22} /> {t("Two-factor")}</div>
              <h1 className="text-3xl font-extrabold text-white">{t("Enter your code")}</h1>
              <p className="text-zinc-400 text-sm mt-1">{useBackup ? t("Enter one of your backup codes.") : t("Enter the 6-digit code from your authenticator app.")}</p>
              <input data-testid="totp-code-input" value={code} onChange={(e) => setCode(useBackup ? e.target.value : e.target.value.replace(/\D/g, ""))} maxLength={useBackup ? 20 : 6}
                inputMode={useBackup ? "text" : "numeric"} placeholder={useBackup ? "xxxx-xxxx" : "000000"}
                className="w-full text-center tracking-[0.3em] font-mono text-2xl bg-[#141416] border border-zinc-800 text-white rounded-xl px-4 py-4 mt-6 focus:border-[#F26722] focus:outline-none" />
              <OrangeButton data-testid="totp-verify-btn" type="submit" disabled={loading} className="mt-4">{loading ? <Loader2 className="animate-spin" size={20} /> : t("Verify")}</OrangeButton>
              <button type="button" onClick={() => { setUseBackup(!useBackup); setCode(""); }} className="w-full text-center text-sm text-zinc-400 mt-4" data-testid="toggle-backup-btn">{useBackup ? t("Use authenticator app instead") : t("Use a backup code")}</button>
            </form>
          )}

          {step === "enroll" && enroll && (
            <form onSubmit={submitEnable} className="px-6 md:px-0 py-10 md:py-0 fade-up">
              <div className="flex items-center gap-2 text-[#F26722] font-bold mb-2"><ShieldCheck size={22} /> {t("Set up 2FA")}</div>
              <h1 className="text-2xl font-extrabold text-white">{t("Scan this QR code")}</h1>
              <p className="text-zinc-400 text-sm mt-1">{t("Open Google Authenticator or Authy, scan the code, then enter the 6-digit code to activate.")}</p>
              <div className="bg-white p-3 rounded-2xl w-fit mx-auto mt-5"><img src={enroll.qr_png} alt="QR" className="w-52 h-52" data-testid="totp-qr" /></div>
              <div className="flex items-center justify-between bg-[#141416] border border-zinc-800 rounded-xl px-4 py-3 mt-4">
                <code className="font-mono text-sm text-zinc-200 break-all">{enroll.manual_key}</code>
                <button type="button" onClick={copyKey} className="text-zinc-400 shrink-0 ml-2" data-testid="copy-secret-btn">{copied ? <Check size={18} className="text-emerald-400" /> : <Copy size={18} />}</button>
              </div>
              <input data-testid="totp-code-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000"
                className="w-full text-center tracking-[0.5em] font-mono text-2xl bg-[#141416] border border-zinc-800 text-white rounded-xl px-4 py-4 mt-5 focus:border-[#F26722] focus:outline-none" />
              <OrangeButton data-testid="totp-verify-btn" type="submit" disabled={loading || code.length !== 6} className="mt-4">{loading ? <Loader2 className="animate-spin" size={20} /> : t("Activate 2FA")}</OrangeButton>
            </form>
          )}

          {step === "backup" && (
            <div className="px-6 md:px-0 py-12 md:py-0 fade-up">
              <div className="flex items-center gap-2 text-[#F26722] font-bold mb-2"><KeyRound size={22} /> {t("Backup codes")}</div>
              <h1 className="text-2xl font-extrabold text-white">{t("Save your backup codes")}</h1>
              <p className="text-zinc-400 text-sm mt-1">{t("Store these somewhere safe. Each can be used once if you lose your phone. They won't be shown again.")}</p>
              <div className="grid grid-cols-2 gap-2 mt-5 font-mono text-sm text-zinc-200" data-testid="backup-codes">
                {backupCodes.map((c) => <div key={c} className="bg-[#141416] border border-zinc-800 rounded-md px-2 py-2 text-center">{c}</div>)}
              </div>
              <label className="flex items-center gap-2 mt-4 text-sm text-zinc-300"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="ack-codes-checkbox" /> {t("I have saved my backup codes")}</label>
              <OrangeButton data-testid="finish-enroll-btn" disabled={!ack} onClick={() => finishLogin(pendingToken, { ...pendingUser, mfa_enabled: true })} className="mt-5">{t("Continue")}</OrangeButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
